import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import Spinner from '@components/Spinner'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { preparePendoMarkAsViewedEvent } from '@modules/pendo'
import { usePendoTrackEvent } from '@modules/pendo/hooks/usePendoTrackEvent'
import { PendoEventType } from '@modules/pendo/types'
import { useSearchParamsForSavedSearch } from '@modules/search/hooks/useSearchProfiles'
import { Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { colors } from '@theme/variables'

import savedSearchApi from '../api'
import {
  MarkAllAsViewed,
  MarkAsViewed,
  MarkAsViewedButtonContainer,
  MarkAsViewedButtonsContainer,
  NewLabelOuter,
} from './index.styles'

const MarkAsViewedButton = ({ savedSearchId, profileId }: { savedSearchId: number; profileId: number }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { user } = useAuthContext()
  const { trackPendoEvent } = usePendoTrackEvent()

  const { mutate: markViewedProfiles } = useMutation({
    mutationFn: savedSearchApi.markViewedProfiles.fn,
    onSuccess: (_: unknown, data) => {
      queryClient.setQueryData<(string | number)[]>([savedSearchApi.runSavedSearch.key, savedSearchId], (oldData = []) => {
        return [...oldData, ...data.profileIds]
      })
      queryClient.invalidateQueries({ queryKey: [savedSearchApi.runSavedSearch.key, savedSearchId] })
      queryClient.invalidateQueries({ queryKey: [savedSearchApi.getSavedSearches.key] })
    },
  })

  const { mutate: markAllProfilesViewed, isPending: isMarkingAllProfilesViewedLoading } = useMutation({
    mutationFn: savedSearchApi.markAllProfilesViewed.fn,
    onSuccess: (_: unknown) => {
      queryClient.invalidateQueries({ queryKey: [savedSearchApi.runSavedSearch.key, savedSearchId] })
      queryClient.invalidateQueries({ queryKey: [savedSearchApi.getSavedSearches.key] })
    },
  })

  const handleMarkProfileAsRead = () => {
    markViewedProfiles({ id: savedSearchId, profileIds: [profileId] })

    const pendoEvent = preparePendoMarkAsViewedEvent({
      eventType: 'mark_as_read',
      savedSearchId,
      user,
    })
    trackPendoEvent(PendoEventType.MarkAsReadClicked, pendoEvent)
  }

  const searchParams = useSearchParamsForSavedSearch()

  return (
    <MarkAsViewedButtonContainer
      onMouseEnter={() => {
        const pendoEvent = preparePendoMarkAsViewedEvent({
          eventType: 'hover',
          savedSearchId,
          user,
        })
        trackPendoEvent(PendoEventType.NewButtonHovered, pendoEvent)
      }}
    >
      <NewLabelOuter>
        <Typography variant="caption" color={colors.white}>
          {t(translationKeys.NEW)}
        </Typography>
      </NewLabelOuter>

      <MarkAsViewedButtonsContainer>
        <MarkAsViewed variant="contained" onClick={handleMarkProfileAsRead}>
          <Typography variant="caption" color={colors.white}>
            {t(translationKeys.MARK_AS_READ)}
          </Typography>
        </MarkAsViewed>

        <MarkAllAsViewed
          variant="outlined"
          onClick={() => {
            markAllProfilesViewed({ id: savedSearchId, searchParams })
            const pendoEvent = preparePendoMarkAsViewedEvent({
              eventType: 'mark_all_as_read',
              savedSearchId,
              user,
            })
            trackPendoEvent(PendoEventType.MarkAllAsReadClicked, pendoEvent)
          }}
        >
          <Typography variant="caption" color={colors.primary}>
            {t(translationKeys.MARK_ALL_AS_READ)}
          </Typography>
          {isMarkingAllProfilesViewedLoading && <Spinner size={10} />}
        </MarkAllAsViewed>
      </MarkAsViewedButtonsContainer>
    </MarkAsViewedButtonContainer>
  )
}

export default MarkAsViewedButton
